import React from 'react';
import { applyPackagePreset, packagePresets } from '../packagePresets';
import { normalizeCanadianPostalCode } from '../../../../helpers/canadianLocation';

const COUNTRY_NAMES = {
    US: 'the United States',
    CA: 'Canada',
    GB: 'the United Kingdom',
    AU: 'Australia',
    NZ: 'New Zealand',
    IE: 'Ireland',
};

const toNumberOrEmpty = (value) => {
    if (value === '') return '';
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? '' : parsed;
};

const displayWeight = (weight, unit) => {
    if (weight === '' || weight === null || typeof weight === 'undefined') return '';
    const numeric = parseFloat(weight);
    if (Number.isNaN(numeric)) return '';
    return unit === 'oz' ? Number((numeric * 16).toFixed(2)) : numeric;
};

const storedWeight = (weight, unit) => {
    if (weight === '') return '';
    const numeric = parseFloat(weight);
    if (Number.isNaN(numeric)) return '';
    return unit === 'oz' ? Number((numeric / 16).toFixed(4)) : numeric;
};

export default function PackageStep({ state, setField, setFields }) {
    const pickupOnly = state.mode === 'pickup';
    const weightUnit = state.weightUnit || 'lbs';
    const isCanadianOrigin = state.originCountry === 'CA';
    const originName = COUNTRY_NAMES[state.originCountry] || state.originCountry;

    const handlePreset = (preset) => {
        setFields(applyPackagePreset(preset));
    };

    const handleWeightChange = (value) => {
        setFields({
            weight: storedWeight(value, weightUnit),
            packagePreset: state.packagePreset && state.packagePreset !== 'custom' ? 'custom' : state.packagePreset,
        });
    };

    const handleUnitChange = (nextUnit) => {
        setField('weightUnit', nextUnit);
    };

    const handleDimensionChange = (field, value) => {
        setFields({
            [field]: toNumberOrEmpty(value),
            packagePreset: state.packagePreset && state.packagePreset !== 'custom' ? 'custom' : state.packagePreset,
        });
    };

    return (
        <div className="shipping-step">
            <h3>{pickupOnly ? 'Pickup Area' : 'Package Details'}</h3>

            {!pickupOnly && (
                <div className="group">
                    <label>Package Preset</label>
                    <div className="shipping-preset-list">
                        {packagePresets.map((preset) => {
                            const selected = state.packagePreset === preset.key;
                            return (
                                <button
                                    key={preset.key}
                                    type="button"
                                    className={`shipping-preset-option${selected ? ' selected' : ''}`}
                                    onClick={() => handlePreset(preset)}
                                    aria-pressed={selected}
                                >
                                    <strong>{preset.name}</strong>
                                    <span>{preset.description}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="group">
                <label>
                    {isCanadianOrigin
                        ? 'Postal Code You\'re Shipping From'
                        : 'ZIP Code You\'re Shipping From'}
                </label>
                <input
                    type="text"
                    value={state.originZIPCode || ''}
                    onChange={(e) => setField('originZIPCode', e.target.value)}
                    onBlur={() => {
                        if (isCanadianOrigin) {
                            setField(
                                'originZIPCode',
                                normalizeCanadianPostalCode(state.originZIPCode),
                            );
                        }
                    }}
                    placeholder={isCanadianOrigin ? 'eg: A1A 1A1' : 'eg: 12345'}
                    inputMode={isCanadianOrigin ? 'text' : 'numeric'}
                    autoCapitalize={isCanadianOrigin ? 'characters' : undefined}
                />
                <p className="help">
                    Shipping from {originName}, based on your storefront Location.
                </p>
            </div>

            {!pickupOnly && (
                <>
                    <div className="group">
                        <label>Package Weight</label>
                        <div className="shipping-inline-fields">
                            <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={displayWeight(state.weight, weightUnit)}
                                onChange={(e) => handleWeightChange(e.target.value)}
                            />
                            <select
                                value={weightUnit}
                                onChange={(e) => handleUnitChange(e.target.value)}
                                aria-label="Weight unit"
                            >
                                <option value="lbs">lbs</option>
                                <option value="oz">oz</option>
                            </select>
                        </div>
                    </div>

                    <div className="split">
                        <div className="group">
                            <label>Length in Inches</label>
                            <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={state.length || ''}
                                onChange={(e) => handleDimensionChange('length', e.target.value)}
                            />
                        </div>
                        <div className="group">
                            <label>Width in Inches</label>
                            <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={state.width || ''}
                                onChange={(e) => handleDimensionChange('width', e.target.value)}
                            />
                        </div>
                        <div className="group">
                            <label>Height in Inches</label>
                            <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={state.height || ''}
                                onChange={(e) => handleDimensionChange('height', e.target.value)}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
